import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import { div as DivMotion } from 'motion/react-client';
import { Box, Button, Callout, Row, Typography, useOverlayContext, useStateInRef } from 'seagull';
import { Bulb } from 'seagull/icons';
import { useResponsive } from '~/client-flow/contexts';
import { useStore, useStoreActions } from '~/contexts';
import { PlaidMode, usePlaidState } from '~/hooks/usePlaidState';
import { useReloginPlaidBankAccount } from '~/hooks/useReloginPlaidBankAccount';
import { useVerifyPlaidBankAccount } from '~/hooks/useVerifyPlaidBankAccount';
import { useLocaleKeys } from '~/i18n';
import { paymentMethodModel } from '~/models';
import { PlaidBankAccountStatusTypes } from '~/models/payment-method';
import { selectors } from '~/store/state-selectors';
import { helpArticles } from '~/utils/helpArticles';
import ExternalLinkButton from '../ExternalLinkButton';
import styles from './BankAccountPanel.module.scss';
import { mapErrorToMessage } from './errors';
import PaymentMethodPanel from './PaymentMethodPanel';
function ManualVerificationCallout(props) {
    const { intent, hasCompleteVerificationAction, hasPermissionToVerify, text, id, verifyable, relationshipId, customerId, vendorId, setNewVerificationStatus, onSuccess, onVerify } = props;
    const { t: texts } = useLocaleKeys('components.bankAccountPanel');
    const verifyPlaidBankAccount = useVerifyPlaidBankAccount();
    const { showErrorToast } = useOverlayContext();
    const plaidLink = usePlaidState({
        mode: {
            type: PlaidMode.Customer,
            relationshipId,
            customerId,
            vendorId,
            fundingSourceToReplace: id
        },
        onSuccess (account) {
            onSuccess(account, {
                type: 'replace',
                fundingSourceId: id
            });
        },
        onError (error) {
            showErrorToast(mapErrorToMessage(error), 'failed-restart-toast');
        }
    });
    function openPlaid() {
        plaidLink.open();
    }
    return /*#__PURE__*/ _jsx(Callout, {
        variant: "paragraph2",
        weight: "light",
        intent: intent,
        testId: "payment-method-callout",
        withAction: hasCompleteVerificationAction && verifyable && /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Button, {
                    skin: "darkSecondary",
                    testId: "restart-bank-payment",
                    onClick: ()=>openPlaid(),
                    size: "small",
                    bi: {
                        name: 'restart_bank_verification'
                    },
                    children: texts.restart()
                }),
                /*#__PURE__*/ _jsx(Button, {
                    size: "small",
                    skin: "primary",
                    testId: 'payment-method-complete-verification',
                    disabled: !hasPermissionToVerify,
                    bi: {
                        name: 'complete_bank_verification'
                    },
                    onClick: ()=>{
                        verifyPlaidBankAccount({
                            fundingSourceId: id,
                            relationshipId,
                            onVerify () {
                                // Optimistic UI update.
                                setNewVerificationStatus(PlaidBankAccountStatusTypes.ManuallyVerified);
                                onVerify(id);
                            },
                            onVerifyFailed () {
                                // Optimistic UI update.
                                setNewVerificationStatus(PlaidBankAccountStatusTypes.VerificationFailed);
                            }
                        });
                    },
                    children: texts.completeVerification()
                })
            ]
        }),
        children: text
    });
}
function AutomaticVerificationCallout(props) {
    const { intent, hasPermissionToVerify, text, id, verificationStatus, verifyable, relationshipId } = props;
    const { t: texts } = useLocaleKeys('components.bankAccountPanel');
    const getProps = useStateInRef(props);
    const isVendor = useStore(selectors.user.isVendor);
    const { api } = useStoreActions();
    const [loginRequired, setLoginRequired] = useState(false);
    const reloginPlaidBankAccount = useReloginPlaidBankAccount();
    const queryIsLoginRequired = useCallback(()=>api.plaid.isLoginRequired(getProps().id).then(setLoginRequired), []);
    useEffect(()=>{
        if (verifyable && verificationStatus === PlaidBankAccountStatusTypes.PendingAutomaticVerification) {
            queryIsLoginRequired();
        }
    }, [
        verificationStatus,
        verifyable,
        queryIsLoginRequired
    ]);
    return /*#__PURE__*/ _jsx(Callout, {
        variant: "paragraph2",
        weight: "light",
        intent: intent,
        testId: "payment-method-callout",
        withAction: loginRequired && verifyable && /*#__PURE__*/ _jsx(Button, {
            size: "small",
            skin: "darkSecondary",
            testId: 'payment-method-reconnect',
            disabled: !hasPermissionToVerify || isVendor,
            bi: {
                name: 'reconnect_bank_account'
            },
            onClick: ()=>{
                reloginPlaidBankAccount({
                    fundingSourceId: id,
                    relationshipId,
                    onRelogin () {
                        api.plaid.loginRepaired(id).then(queryIsLoginRequired);
                    }
                });
            },
            children: loginRequired ? texts.reconnect() : texts.completeVerification()
        }),
        children: loginRequired ? texts.reAuthRequired() : text
    });
}
function GenericWarningCallout(props) {
    const { text, intent } = props;
    return /*#__PURE__*/ _jsx(Callout, {
        variant: "paragraph2",
        weight: "light",
        intent: intent,
        testId: "payment-method-callout",
        children: text
    });
}
function getCallout(param) {
    let { bankAccount, verificationStatus, isProposal, hasPermissionsToVerify, verifyable, relationshipId, customerId, vendorId, setNewVerificationStatus, onSuccess, onVerify, texts } = param;
    if (paymentMethodModel.isErroredBankAccount(bankAccount.bankAccount)) {
        return /*#__PURE__*/ _jsx(GenericWarningCallout, {
            intent: "error",
            text: bankAccount.bankAccount.errorMessage
        });
    } else if (paymentMethodModel.isVerifyWithStripe(bankAccount)) {
        return /*#__PURE__*/ _jsx(GenericWarningCallout, {
            intent: "warning",
            text: texts.verificationFailed()
        });
    } else if (paymentMethodModel.isFailedDueToDuplicate(bankAccount)) {
        return /*#__PURE__*/ _jsx(GenericWarningCallout, {
            intent: "warning",
            text: texts.duplicateBankAccount()
        });
    } else {
        switch(verificationStatus){
            case PlaidBankAccountStatusTypes.PendingAutomaticVerification:
                return /*#__PURE__*/ _jsx(AutomaticVerificationCallout, {
                    intent: isProposal ? 'info' : 'warning',
                    text: isProposal ? texts.autoVerificationProposal() : texts.autoVerificationInProgress(),
                    hasPermissionToVerify: hasPermissionsToVerify,
                    id: bankAccount.id,
                    verificationStatus: verificationStatus,
                    verifyable: verifyable && !isProposal,
                    relationshipId: relationshipId,
                    setNewVerificationStatus: setNewVerificationStatus
                });
            case PlaidBankAccountStatusTypes.PendingManualVerification:
                return /*#__PURE__*/ _jsx(ManualVerificationCallout, {
                    intent: isProposal ? 'info' : 'warning',
                    text: isProposal ? texts.manualVerificationProposal() : texts.manualVerificationPending(),
                    hasCompleteVerificationAction: !isProposal,
                    hasPermissionToVerify: hasPermissionsToVerify,
                    id: bankAccount.id,
                    verifyable: verifyable,
                    relationshipId: relationshipId,
                    customerId: customerId,
                    vendorId: vendorId,
                    setNewVerificationStatus: setNewVerificationStatus,
                    onSuccess: onSuccess,
                    onVerify: onVerify
                });
            case PlaidBankAccountStatusTypes.VerificationFailed:
            case PlaidBankAccountStatusTypes.VerificationExpired:
                return /*#__PURE__*/ _jsx(GenericWarningCallout, {
                    intent: "warning",
                    text: texts.verificationFailed()
                });
            default:
                return null;
        }
    }
}
export default function BankAccountPanel(props) {
    const { t: texts } = useLocaleKeys('components.bankAccountPanel');
    const { isMobile } = useResponsive();
    const { bankAccount, isProposal, apiGatewayHost, isSelected, verifyable, hasPermissionToVerify = false, relationshipId, customerId, vendorId, onSuccess, onVerify, isPaymentMethodInMotion } = props;
    const [newVerificationStatus, setNewVerificationStatus] = useState(null);
    const callout = getCallout({
        bankAccount,
        verificationStatus: newVerificationStatus || paymentMethodModel.verificationStatus(bankAccount),
        isProposal,
        hasPermissionsToVerify: hasPermissionToVerify,
        verifyable: !!verifyable,
        relationshipId,
        customerId,
        vendorId,
        setNewVerificationStatus,
        onSuccess,
        onVerify,
        texts
    });
    const getFooter = ()=>{
        if (callout != null) {
            return /*#__PURE__*/ _jsx("div", {
                className: styles.footerContent,
                children: callout
            });
        }
        const useInlineCompanyIds = isProposal;
        const companyIdsContent = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                texts.achWhitelistBannerText(),
                /*#__PURE__*/ _jsx(ExternalLinkButton, {
                    openInNewTab: true,
                    variant: isMobile ? 'paragraph3' : 'paragraph2',
                    href: helpArticles.achUnauthorizedFailures(),
                    bi: {
                        name: 'ach_whitelist_company_ids_link'
                    },
                    children: texts.achWhitelistBannerCompanyIdsLink()
                }),
                "."
            ]
        });
        return /*#__PURE__*/ _jsx(AnimatePresence, {
            children: isSelected && !isPaymentMethodInMotion && /*#__PURE__*/ _jsx(DivMotion, {
                initial: {
                    height: 0,
                    opacity: 0
                },
                animate: {
                    height: 'auto',
                    opacity: 1
                },
                transition: {
                    duration: 0.3,
                    delay: 0.5,
                    ease: 'easeInOut'
                },
                exit: {
                    height: 0,
                    opacity: 0,
                    transition: {
                        duration: 0.3,
                        delay: 0,
                        ease: 'easeInOut'
                    }
                },
                style: {
                    overflow: 'hidden'
                },
                children: useInlineCompanyIds ? /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "center",
                    className: styles.companyIdsInline,
                    testId: "company-ids-callout",
                    children: [
                        /*#__PURE__*/ _jsx(Box, {
                            className: styles.companyIdsIcon,
                            children: /*#__PURE__*/ _jsx(Bulb, {})
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: isMobile ? 'paragraph3' : 'paragraph2',
                            weight: "light",
                            as: "span",
                            children: companyIdsContent
                        })
                    ]
                }) : /*#__PURE__*/ _jsx(Callout, {
                    testId: "company-ids-callout",
                    variant: isMobile ? 'paragraph3' : 'paragraph2',
                    weight: "light",
                    intent: "info",
                    children: companyIdsContent
                })
            }, "company-ids-callout")
        });
    };
    return /*#__PURE__*/ _jsx(PaymentMethodPanel, _object_spread_props(_object_spread({}, props), {
        title: texts.title(),
        description: texts.accountNumber({
            last4: paymentMethodModel.last4(bankAccount)
        }),
        logo: /*#__PURE__*/ _jsx("img", {
            "data-testid": "bank-account-logo",
            alt: "bank account logo",
            src: "".concat(apiGatewayHost, "/institution/").concat(paymentMethodModel.institutionId(bankAccount) || 'default', "/logo.png")
        }),
        footer: getFooter()
    }));
}
